import React, {useEffect, useReducer, useRef, useState} from 'react';
import {makeStyles} from '@mui/styles';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import SendIcon from '@mui/icons-material/Send';
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Badge,
    Box,
    Card,
    CardContent,
    FormControl,
    Select,
    Tab,
    useTheme
} from "@mui/material";
import {firestore} from "../firebase";
import LoadingComponent from "../pages/LoadingComponent";
import IconButton from "@mui/material/IconButton";
import {useGroup} from "../contexts/GroupContext";
import {grey} from "@mui/material/colors";
import Typography from "@mui/material/Typography";
import {parseTimestamp} from "./Utils";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MenuItem from "@mui/material/MenuItem";
// import { ResizableBox } from 'react-resizable';

const Chat = () => {
    const {group, name, sendUserInput, botNames, botSerials, selectedBot, setSelectedBot, setSelectedSerial} = useGroup() //Get the group and sendUserInput function from the group context
    const scrollRef = useRef(null); //Used for scrolling text into view

    const [newMessages, setNewMessages] = useState(false)
    const [robotProfiles, setRobotProfiles] = useState()    //Front end for the robots
    const [ready, setReady] = useState(false)     //Render component when true
    const [currentMessages, setCurrentMessages] = useState({}) //Currently displayed messages
    const [robotMessages, dispatch] = useReducer(messageReducer, {}); //Updates robotMessages on snapshot update
    const [chatOpen, setChatOpen] = useState(true)
//    const [recentMessages, setRecentMessages] = useState({})
    const theme = useTheme()

    function messageReducer(messageState, newData) {
        let newMessages = JSON.parse(JSON.stringify(messageState)) //Copy to avoid mutating state directly
        if (newData.type === "reset") {
            //Completely reset the message state
            newMessages = {}
            newMessages.data = []
            newMessages.temp = []
            return newMessages
        }

        if (newMessages === undefined) { //If a robot id hasn't been seen before, initialize state
            newMessages = {}
            newMessages.data = []
            newMessages.temp = []
        }

        if (newData.type === "temp") {
            newMessages.temp.push(newData.data) //Add the new message data to temp
        } else {
            setNewMessages(true)
            newMessages.temp = [] //Clear temp
            newData.data.forEach(newDataElement => {
                const matchReducer = (acc, curr) => acc || curr.time === newDataElement.time
                const alreadyHandled = newMessages.data.reduce(matchReducer, false)
                if (!alreadyHandled) {
                    newMessages.data.push(newDataElement) //Add the new message data permanently
                }
            })
        }

        return newMessages
    }

    //Constructor
    useEffect(() => {
        if (group !== undefined) {
            //Generates the front end for each robot. Since it's only used once, define it in constructor
            const generateRobotProfile = (robotName) => {
                let color = grey[500]
                const label = (
                    <Box display="flex">
                        <FiberManualRecordIcon style={{color: color}}/>
                        {robotName}
                    </Box>
                )
                return (
                    <Tab label={label} key={robotName} value={robotName}/>
                )
            }

            //Generates listeners on each robots' dialogueHistory collection
            const generateDialogueListeners = async (robotId) => {
                const col = firestore.collection(`groups/${group}/robots`)
                const dialogueCol = col.doc(robotId).collection("dialogueHistory")

                //Set the listener to dispatch on added message. Return unsubscriber
                const unsubs = await dialogueCol.orderBy("time", "desc").limit(30).onSnapshot(snapshot => {
                    let addedMessages = []
                    snapshot.docChanges().reverse().forEach(change => {
                        if (change.type === 'added') {
                            addedMessages.push(change.doc.data())
                        }
                    })
                    //EW: Moved this out of above forEach loop so we add messages in bulk
                    //    rather than dispatch per message. Was causing significant slowdown
                    if (addedMessages.length !== 0) {
                        dispatch({data: addedMessages})
                    }
                });

                return unsubs
            }

            //Generate profiles and unsubscribers, and initializes listeners
            let currentUnsubscribers = []
            const generateRobotData = async (names) => {
                let profiles = []
                for (const name of names) {
                    let index = botNames.indexOf(name)
                    let id = botSerials[index]
                    profiles.push(generateRobotProfile(name.split(":")[0]))
                    generateDialogueListeners(id).then((r) => {
                        currentUnsubscribers.push(r)
                    })
                }
                return {profiles}
            }

            //Generates all basic data needed before rendering
            generateRobotData(botNames).then((r) => {
                if (r !== undefined) {
                    setRobotProfiles(r.profiles)
                }
            })

            return () => {
                currentUnsubscribers.forEach((unsub) => {
                    unsub()
                })
                //Manually clears the messages for all robot profiles - this will only work
                //in the truest sense in the single zno case - otherwise it could leave
                //entries from robots that different groups do not share - TODO: Will
                dispatch({type: "reset"})
            }

        }
    }, [group, botNames])

    //Once all the robotMessages are populated, we can set which ones will be rendered
    useEffect(() => {
        if (robotMessages !== undefined && robotMessages !== {}) {
            //Starting off without a selectedBot leaves messages uninitialized and throws exceptions
            if (robotMessages.data === undefined) {
                robotMessages.data = []
                robotMessages.temp = []
            }
            //TODO: EW: this is working as intended, though I feel like we should revisit our
            //  "group chat" style. We should probably give an option to only show individual
            //  agent histories
            //sort robot messages by time so they are interleaved, rather than be agent by agent
            robotMessages.data = [].concat(robotMessages.data).sort((a,b) => a.time > b.time ? 1 : -1)
            setCurrentMessages(robotMessages)
        }
    }, [robotMessages])

    //Every time currentMessages is updated, make sure we can see the last sent message
    useEffect(() => {
        if (newMessages) {
            document.title = "[*] Zeeno Control"
        } else {
            document.title = "Zeeno Control"
        }
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [currentMessages, newMessages]);

    //Once everything is defined, we're ready to render
    useEffect(() => {
        if (currentMessages !== undefined && robotProfiles !== undefined && robotMessages !== undefined && selectedBot !== undefined) {
            setReady(true)
        } else {
            setReady(false)
        }
    }, [robotMessages, robotProfiles, currentMessages, selectedBot])

    //Currently this is called for every message on raw data. Maybe it would be best to store the ListItem in a state?
    const generateMessage = (msgData, type) => {
        let styling = ""
        if (type === "temp") {
            styling = classes.tempMessage //Grey it out if it hasn't been processed
        }
        let alignment = "flex-end"
        let speaker = msgData.speaker
        let addressee = msgData.listener
        let bgColor = theme.palette.custom.backgroundTwo
        if (botNames.includes(msgData.speaker)) {
            alignment = "flex-start"
            //TODO: manually define new colors we can map to single name across components
            bgColor = msgData.speaker === selectedBot ? theme.palette.custom.selectedBot : theme.palette.custom.otherBot
        }
        return (
            //It appears as if sometimes messages and their responses both come in at the same millisecond
            // - this random adjustment seems to help with unique keys, but a more logically valid solution
            //should probably be found TODO: Will
            <ListItem key={msgData.time + Math.random()}>
                <Grid container justifyContent={alignment} spacing={1}>
                    <Grid item xs={5}>
                        <Card elevation={2} className={styling}>
                            <CardContent style={{backgroundColor: bgColor}}>
                                <Typography variant="body1">{msgData.text}</Typography>
                                <Typography variant="caption">
                                    {type === "temp" ? "Pending... " + speaker + "->" + addressee : parseTimestamp(msgData.time) + " " + speaker + "->" + addressee}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </ListItem>
        )
    }

    //Called whenever the user sends a message via the chat window
    const handleSendMessage = () => {
        if (selectedBot === "") {
            alert("Please select an agent to send inputs to before sending commands")
            return
        }
        const text = document.getElementById('newMessage').value;
        document.getElementById('newMessage').value = ""
        let msgData = {
            text: text,
            speaker: name,
            listener: selectedBot,
            time: new Date().getTime()
        }
        dispatch({data: msgData, type: "temp"})
        sendUserInput(text, selectedBot) //Updates firebase with the new message
    }

    //Handles if the user hits the enter key while typing
    const handleEnter = (e) => {
        if (e.key === "Enter") {
            handleSendMessage()
        }
    }

    //Handles the user selecting a new bot to chat with
    const handleSelectBot = (event) => {
        //is index stored in event somewhere?
        let pos = botNames.indexOf(event.target.value);
        setSelectedBot(event.target.value);
        setSelectedSerial(botSerials[pos])
    };

    const useStyles = makeStyles((theme) => ({
        messageArea: {
            height: '50vh',
            overflowY: 'auto',
            backgroundColor: theme.palette.custom.backgroundThree,
            "&::-webkit-scrollbar": {
                width: "5px",
            },

            "&::-webkit-scrollbar-track": {
                boxShadow: "inset 0 0 5px " + theme.palette.custom.backgroundTwo,
                background: theme.palette.custom.backgroundTwo,
                borderRadius: "10px",
            },

            "&::-webkit-scrollbar-thumb": {
                background: theme.palette.custom.button,
                borderRadius: "10px",
            },

            // "&::-webkit-scrollbar-thumb:hover": {
            //     background: "rgb(255, 251, 251)",
            // }
        },
        tempMessage: {
            backgroundColor: '#e0e0e088'
        },
    }));

    const classes = useStyles();

    function handleChatOpen(open) {
        setChatOpen(!open)
        setNewMessages(false)
    }

    //EW: what was this search bar supposed to be used for?
    //                                    <Grid item xs={12} style={{padding: '10px'}}>
    //                                        <TextField id="search" label="Search" variant="outlined" fullWidth/>
    //                                    </Grid>
    //                                    <Divider/>
    if (ready) {
        return (
            <Box>
                <Paper >
                    <Accordion expanded={chatOpen}>
                        <AccordionSummary  onClick={() => handleChatOpen(chatOpen)}>
                            <Grid container direction="row"
                                  justifyContent="space-between"
                                  alignItems="center">
                                <Grid item>
                                    <Typography variant="h1" color={'accordionHeader'}>Message History</Typography>
                                </Grid>
                                <Grid item>
                                    <IconButton size="small" onClick={() => handleChatOpen(chatOpen)}>
                                        {chatOpen ? <KeyboardArrowUpIcon fontSize="large"/> :
                                            <Badge color="secondary" variant="dot" invisible={!newMessages}>
                                                <KeyboardArrowDownIcon fontSize="large"/>
                                            </Badge>}
                                    </IconButton>
                                </Grid>
                            </Grid>
                        </AccordionSummary>
                        <AccordionDetails >
                            <Grid container>
                                <Grid item xs={12}>
                                    <List ref={scrollRef} className={classes.messageArea}>
                                        {currentMessages.data.map(generateMessage)}
                                        {currentMessages.temp.map(tempData => (generateMessage(tempData, "temp")))}
                                    </List>
                                </Grid>
                            </Grid>
                            <Grid  container style={{padding: '20px'}}>
                                <Grid item backgroundColor={theme.palette.custom.backgroundTwo} pr={1} xs={2}>
                                    <Typography>Address Agent:</Typography>
                                    <FormControl fullWidth>
                                        <Select
                                            labelId="groupSwitchFieldLabel"
                                            id="groupSwitchField"
                                            value={selectedBot}
                                            label=""
                                            onChange={handleSelectBot}
                                            variant={'standard'}
                                            disableUnderline={'true'}
                                            InputProps={{'sx':{textAlign:'center'}}}
                                        >
                                            {botNames.map((option) => (
                                                <MenuItem key={option} value={option}>
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={9}>
                                    <TextField  autoComplete='off' id="newMessage" label="Type Something"
                                               onKeyPress={handleEnter} fullWidth/>
                                </Grid>
                                <Grid item xs={1} align="right">
                                    <IconButton onClick={handleSendMessage}>
                                        <SendIcon color='send'/>
                                    </IconButton>
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                </Paper>
            </Box>
        );
    } else {
        return (
            <LoadingComponent/>
        )
    }
}

export default Chat;